import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  memo,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import "./UserQuestion.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import IconSource from "../../common/icons/IconSource";
import IconEllipse from "../../common/icons/IconEllipse";
import IconClose from "../../common/icons/IconClose";
import IconOpenAI from "../../common/icons/IconOpenAI";
import IconPerplexity from "../../common/icons/IconPerplexity";
import { marked } from "marked";
import ContentCard from "../content-card/ContentCard";
import PropTypes from "prop-types";
import AuthorCard from "../author-card/AuthorCard";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import IconSendMail from "../../common/icons/IconSendMail";
import IconCopy from "../../common/icons/IconCopy";
import IconEdit from "../../common/icons/IconEdit";
import ChatEditor from "../chat-editor/ChatEditor";
import {
  AI_BASE_URL,
  API_BASE_URL,
  PAGE_OFFSET,
  SILOS,
} from "../../config/index";
import {
  transformString,
  getFormattedAnswer,
  formatAnswer,
  getFormattedmessage,
} from "../../utils/stringUtils";
import IconReport from "../../common/icons/IconReport";
import ReportPanel from "../report-panel/ReportPanel";
import IconCloseSidebar from "../../common/icons/IconCloseSidebar";
import FrameWrapperArticle from "../framerWrapperArticle/FramerWrapperArticle";
import FrameWrapperRubric from "../frameWrapperRubric/FrameWrapperRubric";
import FrameWrapperPdf from "../frameWrapperPdf/FrameWrapperPdf";
import { Worker } from "@react-pdf-viewer/core";
import { getUserRegistration } from "../../api/event";
import IconDb from "../../common/icons/IconDb";
import "../silos/silos.css";
import { getArticles, getRubrics } from "../../api/blog";

// Simple wrapper component to fetch and display rubric by ID
const RubricByIdWrapper = ({ rubricId, closePopup, token }) => {
  const [rubricData, setRubricData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRubric = async () => {
      try {
        const url = `${API_BASE_URL}/media/faq-rubric?access_token=${token}&filter=[%7B%22property%22:%22id%22,%22value%22:${Math.floor(
          rubricId
        )},%22operator%22:%22eq%22%7D]&fields=*,children,chain,author`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response}`);
        }

        const responseText = await response.text();

        const data = JSON.parse(responseText);

        // Check for data.data format (like in fetchSourceData)
        if (data?.data?.[0]) {
          setRubricData(data.data[0]);
        } else if (data && data.length > 0) {
          setRubricData(data[0]);
        }
      } catch (error) {
        console.error("Error fetching rubric:", error);
        console.error("Error details:", {
          rubricId,
          token: token ? "present" : "missing",
          API_BASE_URL,
        });
      } finally {
        setLoading(false);
      }
    };

    if (rubricId && token) {
      fetchRubric();
    }
  }, [rubricId, token]);

  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>
    );
  }

  if (!rubricData) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        Rubric not found
      </div>
    );
  }

  return (
    <FrameWrapperRubric
      rubric={rubricData}
      type="RUBRIC"
      closePopup={closePopup}
    />
  );
};

// Simple wrapper component to handle PDF with signed URL
const PdfByIdWrapper = ({ pdfId, url, page, closePopup, token, objectKey }) => {
  const [pdfUrl, setPdfUrl] = useState(url || "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentName, setDocumentName] = useState("");
  const extension = url?.split("/").pop().split(".").pop();
  const [baseName] = url?.split(".") ?? ["", ""];

  const isSpecialDocument = useMemo(() => {
    const isPdf = extension?.toUpperCase() === "PDF";
    const isExtracted = url?.includes("_extrait_");
    return isPdf && isExtracted;
  }, [pdfId, url]);

  const extractDocumentName = (url) => {
    try {
      const segments = url.split("/");
      const fileName = segments.pop()?.split("_extrait_")[0];
      const cleanName = decodeURIComponent(fileName).replace(/\.[^/.]+$/, "");
      return cleanName;
    } catch {
      return "";
    }
  };

  const calculateExtractPage = (originalPage, extractUrl) => {
    const extractMatch = extractUrl?.match(/_extrait_(\d+)_(\d+)/);
    return extractMatch
      ? parseInt(extractMatch[2])
      : parseInt(originalPage) || 1;
  };

  const extractObjectKeyFromUrl = (url) => {
    if (!url) return null;

    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname.substring(1);
      const decodedPath = decodeURIComponent(path);

      return decodedPath;
    } catch (error) {
      console.error("Error extracting object key from URL:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchPresignedUrl = (objectKey) => {
      if (!objectKey) {
        setError("No object key provided.");
        setLoading(false);
        return;
      }

      const controller = new AbortController();
      const fetchUrl = `${AI_BASE_URL}/auth/presigned_url?object_key=${encodeURIComponent(
        objectKey
      )}`;
      fetch(fetchUrl, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: "",
        signal: controller.signal,
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.url) {
            setPdfUrl(data.url);
            setLoading(false);
          } else {
            setError("Presigned URL not returned.");
            setLoading(false);
          }
        })
        .catch((err) => {
          console.error("❌ Fetch error:", err);
          if (!controller.signal.aborted) {
            setError("Failed to fetch presigned URL.");
            setLoading(false);
          }
        });

      return () => controller.abort();
    };

    if (pdfUrl?.includes("X-Amz-Algorithm=")) {
      setLoading(false);
      return;
    }

    if (isSpecialDocument && token && url) {
      const objectKey = extractObjectKeyFromUrl(url);
      const docName = extractDocumentName(url);

      setDocumentName(docName);
      if (objectKey) {
        fetchPresignedUrl(objectKey);
      } else {
        setError("Could not extract object key from URL.");
        setLoading(false);
      }
    }

    if (!isSpecialDocument && token && objectKey) {
      const fullKey = `${objectKey}`;

      setDocumentName(pdfId.replace(/\.[^/.]+$/, ""));
      fetchPresignedUrl(fullKey);
    }

    if (!isSpecialDocument && token && objectKey && !url) {
      const fullKey = `${objectKey}`;

      setDocumentName(pdfId.replace(/\.[^/.]+$/, ""));
      fetchPresignedUrl(fullKey);
    }
  }, [pdfUrl, pdfId, url, token, objectKey, isSpecialDocument]);

  const extractPage = calculateExtractPage(page, url);

  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "#444" }}>
        <p>Loading PDF…</p>
        <p>URL: {pdfUrl}</p>
        <p>Special Document: {isSpecialDocument ? "Yes" : "No"}</p>
        <p>Document Name: {objectKey}</p>
      </div>
    );
  }

  if (error || !pdfUrl) {
    return (
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          backgroundColor: "#fff",
          border: "1px solid #ddd",
          borderRadius: "4px",
          margin: "20px",
        }}
      >
        <h3>PDF Unavailable</h3>
        <p>{error || "PDF URL is missing"}</p>
        <button
          onClick={closePopup}
          style={{
            padding: "8px 16px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
      <div
        style={{
          border: "1px solid rgba(0, 0, 0, 0.3)",
          height: "90%",
          width: "70%",
          margin: "0 auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <FrameWrapperPdf
          closePopup={closePopup}
          PdfUrl={pdfUrl}
          page={extractPage}
          isSpecialDocument={isSpecialDocument}
          documentName={documentName}
          content={[]} // optional placeholder
        />
      </div>
    </Worker>
  );
};

// SVG for the info icon
const InfoIcon = ({ width = "12", height = "12" }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M12 7v2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M12 11v6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

// Helper function to find source markdown and replace it with styled HTML cards
const addSourceCardStyling = (text, webSources = [], allSources = []) => {
  if (!text) return { processedText: "", usedSourceIndices: new Set() };
  let processedText = text;
  const usedSourceIndices = new Set();

  const nonVideoSources = allSources.filter(
    (source) => source.type !== "VIDEO" && source.type !== "video"
  );

  // Create a combined sources array for better URL matching
  const combinedSources = [...webSources];
  if (nonVideoSources) {
    nonVideoSources.forEach((source) => {
      if (source.url && !combinedSources.some((ws) => ws.url === source.url)) {
        combinedSources.push(source);
      }
    });
  }

  if (combinedSources.length > 0) {
    const numberedTags = processedText.match(/\[(\d+)\](?!\s*https?:\/\/)/g);

    if (numberedTags) {
      numberedTags.forEach((tag) => {
        const number = parseInt(tag.match(/\[(\d+)\]/)[1]);
        const sourceIndex = number - 1; // Convert to 0-based index

        if (sourceIndex >= 0 && sourceIndex < combinedSources.length) {
          const source = combinedSources[sourceIndex];
          usedSourceIndices.add(sourceIndex);

          if (source.url && source.type !== "VIDEO") {
            try {
              const domain = new URL(source.url).hostname;
              const displayDomain = domain.startsWith("www.")
                ? domain.substring(4)
                : domain;
              const card = `<a href="${source.url}" target="_blank" rel="noopener noreferrer" class="inline-citation-card">${displayDomain}</a>`;
              processedText = processedText.replace(
                new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
                card
              );
            } catch (e) {
              const card = `<a href="${
                source.url
              }" target="_blank" rel="noopener noreferrer" class="inline-citation-card">${
                source.source || source.url
              }</a>`;
              processedText = processedText.replace(
                new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
                card
              );
            }
          } else {
            processedText = processedText.replace(
              new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
              ""
            );
          }
        } else {
          processedText = processedText.replace(
            new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
            ""
          );
        }
      });
    }
  }

  // Note: Citation processing with type/id attributes is now handled after markdown parsing

  // 2. Regex for <cite url="URL">Title</cite> format (with any additional attributes)
  // BUT SKIP if it has type and id attributes (those are handled separately)
  processedText = processedText.replace(
    /<cite\s+[^>]*url="([^"]*)"[^>]*>([^<]*)<\/cite>/g,
    (match, url, title) => {
      // Check if this cite tag has type and id attributes - if so, skip it
      const hasTypeAndId =
        /type="[^"]*"/.test(match) && /id="[^"]*"/.test(match);
      if (hasTypeAndId) {
        return match; // Return unchanged, will be processed by the post-markdown handler
      }

      try {
        const domain = new URL(url).hostname;
        const displayDomain = domain.startsWith("www.")
          ? domain.substring(4)
          : domain;
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="inline-citation-card">${
          title || displayDomain
        }</a>`;
      } catch (error) {
        console.error("Error parsing URL for cite tag:", error);
        // Fallback: use the title if URL is invalid
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="inline-citation-card">${
          title || url
        }</a>`;
      }
    }
  );

  // 3. Handle <cite> tags without URLs by trying to match with available sources
  processedText = processedText.replace(
    /<cite>([^<]*)<\/cite>/g,
    (match, title) => {
      // Try to find a matching source by title/name
      const matchingSource = combinedSources.find(
        (source) =>
          source.source &&
          (source.source.toLowerCase().includes(title.toLowerCase()) ||
            title.toLowerCase().includes(source.source.toLowerCase()))
      );

      if (matchingSource && matchingSource.url) {
        try {
          const domain = new URL(matchingSource.url).hostname;
          const displayDomain = domain.startsWith("www.")
            ? domain.substring(4)
            : domain;
          return `<a href="${matchingSource.url}" target="_blank" rel="noopener noreferrer" class="inline-citation-card">${title}</a>`;
        } catch (error) {
          return `<span class="inline-citation-card">${title}</span>`;
        }
      }

      // If no matching source found, just style it as a citation
      return `<span class="inline-citation-card">${title}</span>`;
    }
  );

  // 4. Regex for the custom markdown format: ([text](url))
  processedText = processedText.replace(
    /\(\[([^\]]*)\]\(([^)]*)\)\)/g,
    (match, text, url) => {
      try {
        const domain = new URL(url).hostname;
        const displayDomain = domain.startsWith("www.")
          ? domain.substring(4)
          : domain;
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="inline-citation-card">${
          text || displayDomain
        }</a>`;
      } catch (error) {
        console.error("Error parsing URL for source card:", error);
        const cleanText = text.replace(/[\[\]()]/g, "");
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="inline-citation-card">${cleanText}</a>`;
      }
    }
  );

  // 5. Remove footnote-style citations at the end of text (but keep inline ones)
  // Remove patterns like "\n\n[1] https://..." at the end
  processedText = processedText.replace(
    /\n\n(\[(\d+)\]\s+(https?:\/\/\S+)\s*)+$/g,
    ""
  );

  // Remove "Source :" citations at the end
  processedText = processedText.replace(
    /\n\n\(Source\s*:\s*([^)]+)\)\s*$/g,
    ""
  );

  return { processedText, usedSourceIndices };
};

const formatSourceName = (sourceName) => {
  if (!sourceName) return "";

  if (sourceName.includes("\\")) {
    sourceName = sourceName.split("\\").pop();
  }
  if (sourceName.includes("/")) {
    sourceName = sourceName.split("/").pop();
  }

  if (sourceName.length > 20) {
    sourceName = sourceName.substring(0, 20) + "...";
  }

  return sourceName;
};

// Helper function to check if a silo has an expiration date
const hasSiloExpiration = (silo) => {
  if (!silo) return false;
  if (typeof silo === "object" && silo.expireAt) return true;
  return false;
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

// Helper function to calculate days until expiration
const daysUntilExpiration = (expireAt) => {
  if (!expireAt) return null;
  const expireDate = new Date(expireAt);
  const now = new Date();
  const diffDays = Math.ceil((expireDate - now) / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : null; // Only return positive values
};

// Helper function to truncate silo names
const truncateSiloName = (name, maxLength = 20) => {
  if (typeof name !== "string") return "";
  if (name.length <= maxLength) return name;
  return `${name.substring(0, maxLength)}...`;
};

// Separate function to process citations on already-parsed HTML
const applyCitationProcessing = (
  htmlText,
  webSources = [],
  allSources = []
) => {
  if (!htmlText) return htmlText;

  const nonVideoSources = allSources.filter(
    (source) => source.type !== "VIDEO" && source.type !== "video"
  );

  const combinedSources = [...webSources];
  if (nonVideoSources) {
    nonVideoSources.forEach((source) => {
      if (source.url && !combinedSources.some((ws) => ws.url === source.url)) {
        combinedSources.push(source);
      }
    });
  }

  const findSourceById = (id) => {
    return nonVideoSources.find((source) => {
      return (
        (source.source && source.source.includes(id)) ||
        (source.name && source.name.includes(id)) ||
        source.id === id
      );
    });
  };

  return htmlText.replace(
    /<cite\s+([^>]*)>([^<]*)<\/cite>/g,
    (match, attributes, title) => {
      const typeMatch = attributes.match(/type="([^"]*)"/);
      const idMatch = attributes.match(/id="([^"]*)"/);
      const pageMatch = attributes.match(/page="([^"]*)"/);
      const urlMatch = attributes.match(/url="([^"]*)"/);
      const objectKeyMatch = attributes.match(/object_key="([^"]*)"/); // 🧩 root level

      if (!(typeMatch && idMatch)) return match;

      const type = typeMatch[1];
      const id = idMatch[1];

      if (type.toLowerCase() === "video") {
        return ""; // Skip video citations
      }

      let page = pageMatch ? pageMatch[1] : null;
      let url = urlMatch ? urlMatch[1] : null;
      let objectKey = objectKeyMatch ? objectKeyMatch[1] : null;

      // 🧠 Fallback from sourceData if needed
      const sourceData = findSourceById(id);
      if (sourceData) {
        page ??= sourceData.page ?? sourceData.target_page ?? null;
        url ??= sourceData.url ?? null;
        objectKey ??= sourceData.objectKey ?? null;
      }

      let dataAttributes = `data-citation-type="${type}" data-citation-id="${id}" data-citation-title="${title}"`;

      if (type === "pdf") {
        if (page) dataAttributes += ` data-citation-page="${page}"`;
        if (url) dataAttributes += ` data-citation-url="${url}"`;
        if (objectKey)
          dataAttributes += ` data-citation-object-key="${objectKey}"`;
      }
      return `<span class="inline-citation-card" ${dataAttributes}>${title}</span>`;
    }
  );
};

const htmlToMarkdown = (html) => {
  let markdown = html;
  markdown = markdown.replace(/<p>(.*?)<\/p>/gi, "$1\n\n");
  markdown = markdown.replace(/<(strong|b)>(.*?)<\/(strong|b)>/gi, "**$2**");
  markdown = markdown.replace(/<(em|i)>(.*?)<\/(em|i)>/gi, "*$2*");
  markdown = markdown.replace(/<h1>(.*?)<\/h1>/gi, "# $1\n\n");
  markdown = markdown.replace(/<h2>(.*?)<\/h2>/gi, "## $1\n\n");
  markdown = markdown.replace(/<h3>(.*?)<\/h3>/gi, "### $1\n\n");
  markdown = markdown.replace(/<ul>(.*?)<\/ul>/gi, "$1");
  markdown = markdown.replace(/<ol>(.*?)<\/ol>/gi, "$1");
  markdown = markdown.replace(/<li>(.*?)<\/li>/gi, "- $1\n");
  markdown = markdown.replace(/<a href="(.*?)">(.*?)<\/a>/gi, "[$2]($1)");
  markdown = markdown.replace(/<br\s*\/?>/gi, "\n");
  markdown = markdown.replace(/<[^>]*>/g, ""); // Clean remaining tags
  markdown = markdown.replace(/&nbsp;/g, " ");
  markdown = markdown.replace(/&amp;/g, "&");
  markdown = markdown.replace(/&lt;/g, "<");
  markdown = markdown.replace(/&gt;/g, ">");
  return markdown.trim();
};

const convertHtmlToPlainText = (html) => {
   // Simple conversion to preserve some structure for mailto
   let text = html;
   text = text.replace(/<tr[^>]*>/gi, "\n"); // Newline for table rows
   text = text.replace(/<\/(td|th)>/gi, " | "); // Separator for cells
   text = text.replace(/<\/(p|div|h[1-6])>/gi, "\n\n");
   text = text.replace(/<\/(table|thead|tbody)>/gi, "\n"); // Spacing for table parts
   text = text.replace(/<br\s*\/?>/gi, "\n");
   text = text.replace(/<[^>]+>/g, ""); // Strip other tags
   text = decodeEntities(text); // You might need a decode helper or just standard decode
   return text.trim();
};

const decodeEntities = (text) => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
};

// Helper: inject inline styles for tables so they persist in email clients
const injectMailStyles = (html) => {
  // Use DOM manipulation for robustness instead of Regex
  const div = document.createElement('div');
  div.innerHTML = html;

  // 1. Clean up empty rows
  const rows = div.querySelectorAll('tr');
  rows.forEach(row => {
    // Get text content, replacing non-breaking spaces with normal spaces to check strictly
    const text = row.textContent.replace(/\u00A0/g, ' ').trim();
    if (!text) {
      row.remove();
    }
  });

  // 2. Clean up empty headers
  div.querySelectorAll('thead').forEach(thead => {
     if (thead.children.length === 0) thead.remove();
  });
  
  // 3. Apply Styles
  div.querySelectorAll('table').forEach(table => {
      table.setAttribute('border', '1');
      table.setAttribute('cellspacing', '0');
      table.setAttribute('cellpadding', '0');
      
      table.style.borderCollapse = 'collapse';
      table.style.width = '100%';
      table.style.borderSpacing = '0';
      table.style.margin = '0';
      table.style.marginBottom = '20px';
      table.style.border = '1px solid #808995';
      table.style.backgroundColor = '#ffffff';
      table.style.color = '#333333';
  });

  div.querySelectorAll('td, th').forEach(cell => {
      cell.style.border = '1px solid #808995';
      cell.style.padding = '10px';
      cell.style.textAlign = 'center';
      cell.style.backgroundColor = '#ffffff';
      cell.style.color = '#333333';
      cell.style.lineHeight = '1.4';
      cell.style.fontSize = '14px'; // Explicit font size helps in some clients
  });
  
  return div.innerHTML;
};


const UserQuestion = ({
  question,
  avatar,
  conversation,
  review,
  user,
  token,
  onChatPosted,
  onRatingComplete,
  setInputDisabled,
  model,
  customPrompt,
  onReviewCreated,
  silos,
  handleNewDiscussion,
  onOpenSilosModal,
  userSilots = [],
}) => {
  const [finalizedReview, setFinalizedReview] = useState(review);
  const hasEverHadReview = useRef(!!review);
  const [userEventsAccess, setUserEventsAccess] = useState(null);

  useEffect(() => {
    if (review && !finalizedReview) {
      setFinalizedReview(review);
      hasEverHadReview.current = true;
    }
    const providerToUse = review?.provider || finalizedReview?.provider;
    if (providerToUse) {
      setAiProvider(providerToUse);
    }
  }, [review, finalizedReview]);

  const shouldShowSiloTags =
    finalizedReview?.silo ||
    (finalizedReview?.silos &&
      Array.isArray(finalizedReview.silos) &&
      finalizedReview.silos.length > 0) || // Existing review with silo data
    !finalizedReview; // New question (no review)

  const [loading, setLoading] = useState(!finalizedReview);
  const [streamedAnswer, setStreamedAnswer] = useState(() => {
    if (finalizedReview?.answer) {
      const { processedText, usedSourceIndices } = addSourceCardStyling(
        finalizedReview.answer
      );
      const parsedHtml = marked.parse(processedText);
      // Combine all available sources for citation processing
      const allAvailableSources = [
        ...(finalizedReview?.sources || []),
        ...(finalizedReview?.sourcesData || []),
      ];
      const citationProcessed = applyCitationProcessing(
        parsedHtml,
        [],
        allAvailableSources
      );
      return { processedText: citationProcessed, usedSourceIndices };
    }
    return { processedText: "", usedSourceIndices: new Set() };
  });
  const [showNote, setshowNote] = useState(false);
  const [answerData, setAnswerData] = useState(null);
  const [versionButtons, setVersionButtons] = useState([]);
  const [sourcesData, setSourcesData] = useState(
    finalizedReview?.sources || []
  ); // Use review sources if available
  const [sources, setSources] = useState([]);
  const [displayCards, setDisplayCards] = useState(!!finalizedReview);
  const [displayMailPanel, setDisplayMailPanel] = useState(false);
  const [displayEditPanel, setDisplayEditPanel] = useState(false);
  const [displayReportPanel, setDisplayReportPanel] = useState(false);
  const [finalSources, setFinalSources] = useState([]);
  const [questionSent, setQuestionSent] = useState(!!finalizedReview); // Track if the question has been sent
  const [lockedSilos, setLockedSilos] = useState(silos); // Store the silos at the time of sending
  const [showActiveSilosModal, setShowActiveSilosModal] = useState(false);
  const answerRef = useRef(null);
  const chatPosted = useRef(false);
  const [reviewSent, setReviewSent] = useState(false);
  const [record, setRecord] = useState(finalizedReview || null);
  const [displayPanel, setDisplayPanel] = useState(false);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const containerRef = useRef(null);
  const [mustRate, setMustRate] = useState(false);
  const [recipient, setRecipient] = useState(
    finalizedReview?.recipient_emails?.join(", ") || ""
  );
  const prefixProcessedRef = useRef(false); // To track if prefix has been processed
  const shouldRenderSourcesRef = useRef(true); // Ref to hold the latest value
  const [webSources, setWebSources] = useState([]); // State for web search sources
  const [showWebSourcesPopover, setShowWebSourcesPopover] = useState(false);

  // Track scroll-related states
  const [userScrolling, setUserScrolling] = useState(false);
  const lastScrollPositionRef = useRef(0);
  const scrollTimeoutRef = useRef(null);
  const scrollActivityRef = useRef(0); // Track how many scroll events occurred
  const isStreamingRef = useRef(false); // Track if answer is currently streaming

  const [subject, setSubject] = useState(
    `Vous m'avez posé la question suivante :\n\n${question}`
  );
  const [message, setMessage] = useState("");

  const editorRef = useRef(null);
  const isEditingRef = useRef(false);

  // Sync message to editor when not editing
  useEffect(() => {
    if (editorRef.current && !isEditingRef.current) {
        const parsed = marked.parse(message || "");
        if (editorRef.current.innerHTML !== parsed) {
            editorRef.current.innerHTML = parsed;
        }
    }
  }, [message, displayMailPanel]); // Trigger when panel opens too

  
  // Sync state with incoming data (for initial load/refresh)
  useEffect(() => {
    const freshAnswer = finalizedReview?.answer || answerData?.content || "";
    // Update if we have a fresh answer and (message is empty OR message matches the old answer format)
    if (freshAnswer) {
         const formatted = getFormattedAnswer(freshAnswer);
         // Only update if it's different to avoid loops, but allow overwriting empty init
         if (formatted !== message && (!message || message.length < 5)) { 
             setMessage(formatted); 
         } else if (!message) {
             setMessage(formatted);
         }
    }
  }, [finalizedReview, answerData]);

  const [editedAnswer, setEditedAnswer] = useState("");

  const [aiProvider, setAiProvider] = useState(
    finalizedReview?.provider || "tamtam"
  );

  const lastLoadedSourcesRef = useRef(null);

  // Add state for tooltip
  const [tagTooltip, setTagTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [citationPopup, setCitationPopup] = useState(null);

  useEffect(() => {
    getUserEventAccess(sourcesData);
  }, [sourcesData]);

  const toggleWebSourcesPanel = () => {
    setShowWebSourcesPopover(!showWebSourcesPopover);
  };

  // Function to handle citation clicks
  const handleCitationClick = (type, id, title, page, url, objectKey) => {
    const newPopup = {
      type: type.toUpperCase(),
      id,
      title,
      page,
      url,
      objectKey,
    };

    if (
      citationPopup &&
      citationPopup.type === newPopup.type &&
      citationPopup.id === newPopup.id &&
      citationPopup.objectKey === newPopup.objectKey
    ) {
      return;
    }

    // Set the new popup
    setCitationPopup(newPopup);
  };

  const closeCitationPopup = () => {
    setCitationPopup(null);
  };

  // Utility function to extract a silo name from various formats
  const getSelectedSilo = (siloData) => {
    // Handle null/undefined
    if (!siloData) return null;

    // Handle string
    if (typeof siloData === "string") {
      // Remove square brackets if present
      return siloData.replace(/^\[|\]$/g, "");
    }

    // Handle object with name/id/title
    if (
      typeof siloData === "object" &&
      siloData !== null &&
      !Array.isArray(siloData)
    ) {
      // Prioritize label (if it exists and is not empty), then name, then id, then title.
      let result =
        siloData.label &&
        typeof siloData.label === "string" &&
        siloData.label.trim() !== ""
          ? siloData.label.trim()
          : siloData.name || siloData.id || siloData.title;
      if (result) {
        // The existing cleaning primarily targets brackets. Labels should be clean.
        return result.toString().replace(/^[\[\]]+|[\[\]]+$/g, ""); // Clean leading/trailing brackets more robustly
      }
      // Last resort - stringify but only if it's not empty
      if (Object.keys(siloData).length > 0) {
        return JSON.stringify(siloData).replace(/^\[|\]$/g, "");
      }
      return null;
    }

    // Handle array of strings
    if (Array.isArray(siloData) && siloData.length > 0) {
      if (typeof siloData[0] === "string") {
        // Remove square brackets if present
        return siloData[0].replace(/^\[|\]$/g, "");
      }

      // Handle array of objects
      const activeSilo = siloData.find((s) => s.active || s.selected);
      if (activeSilo) {
        return getSelectedSilo(activeSilo); // Recursively handle the active silo
      }

      // Use first item
      return getSelectedSilo(siloData[0]); // Recursively handle the first silo
    }

    return null;
  };

  // Get the silo name from review or default to the first silo in the silos array
  const getSiloFromReview = () => {
    // If no review exists, don't return any silo
    if (!finalizedReview) return null;

    if (finalizedReview.silo) {
      return getSelectedSilo(finalizedReview.silo);
    }

    // Then try review.silos
    if (finalizedReview.silos) {
      return getSelectedSilo(finalizedReview.silos);
    }

    return null;
  };

  // Helper to split a string that might contain multiple silos
  const splitMultipleSilos = (siloString) => {
    if (!siloString || typeof siloString !== "string") return [];

    // Check if the string looks like it contains multiple silos with quotes and commas
    if (siloString.includes('","')) {
      // Split by ","
      return siloString
        .split('","')
        .map((s) => s.replace(/^["']|["']$/g, "")) // Remove quotes at start/end
        .filter((s) => s.trim() !== ""); // Remove empty strings
    }

    // Check if it's a comma-separated list without quotes
    if (siloString.includes(",")) {
      return siloString
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s !== "");
    }

    // It's a single silo - remove any quotes
    return [siloString.replace(/^["']|["']$/g, "")];
  };

  const [currentSilo, setCurrentSilo] = useState(getSiloFromReview());

  const getAvailableSources = useCallback(() => {
    if (sourcesData && sourcesData.length > 0) {
      return sourcesData;
    }
    if (sources && sources.length > 0) {
      return sources;
    }
    if (finalSources && finalSources.length > 0) {
      return finalSources;
    }
    return [];
  }, [sourcesData, sources, finalSources]);

  const removeDuplicates = (arr, key) => {
    const seen = new Set();
    return arr.filter((obj) => {
      if (obj[key] && seen.has(obj[key])) {
        return false;
      } else if (obj[key]) {
        seen.add(obj[key]);
        return true;
      }
      return true;
    });
  };
  const fetchArticleData = async (articles) => {
    const articleIds = articles.map(
      (source) => source.id || source.data[0]?.id
    );
    const filters = {
      ids: articleIds,
    };

    const result = await getArticles(filters, token);
    const articlesById = result?.data?.reduce((acc, article) => {
      acc[article.id] = article;
      return acc;
    }, {});

    const enrichedArticles = articles.map((source) => {
      const id = source.id ?? source.data?.[0]?.id;
      return articlesById[id]
        ? {
            data: [articlesById[id]],
            type: source.type,
            similarity: source.similarity,
            recency: source.recency,
            score: source.score,
          }
        : null;
    });
    return enrichedArticles.filter(Boolean);
  };

  const fetchRubricData = async (rubrics) => {
    const rubricIds = rubrics.map((source) => source.id || source.data[0]?.id);
    const filters = {
      ids: rubricIds,
    };

    const result = await getRubrics(filters, token);
    const rubricsById = result?.data?.reduce((acc, rubric) => {
      acc[rubric.id] = rubric;
      return acc;
    }, {});

    const enrichedRubric = rubrics.map((source) => {
      const id = source.id ?? source.data?.[0]?.id;
      return rubricsById[id]
        ? {
            data: [rubricsById[id]],
            type: source.type,
            similarity: source.similarity,
            recency: source.recency,
            score: source.score,
          }
        : null;
    });

    return enrichedRubric.filter(Boolean);
  };

  const fetchSourceData = async (sources) => {
    try {
      const articles = sources.filter((source) => source.type === "ARTICLE");
      const rubrics = sources.filter((source) => source.type === "RUBRIC");
      let sourcesData = [];

      if (articles?.length > 0) {
        sourcesData.push(fetchArticleData(articles));
      }

      if (rubrics?.length > 0) {
        sourcesData.push(fetchRubricData(rubrics));
      }

      const filteredSources = sources.filter(
        (source) => !["ARTICLE", "RUBRIC"].includes(source.type)
      );
      const results = filteredSources.map((source) => {
        if (source.type === "web") {
          return source;
        }
        // Handle the new source format
        if (source.type === "PDF") {
          // For PDF sources, we already have all the data we need
          return {
            ...source,
            type: "PDF",
            extra: source.extra_pages || source.extra,
            content: source.content || [],
          };
        } else if (source.type === "VIDEO" || source.type === "video") {
          return {
            ...source,
            type: "VIDEO",
            event: source.event_id,
          };
        }
        return null;
      });

      sourcesData.push(Promise.resolve(results));
      return sourcesData;
    } catch (error) {
      console.error("Error fetching source data:", error);
      return null;
    }
  };

  const processSources = useCallback(async (sources) => {
    if (!sources || !Array.isArray(sources)) return [];
    const sourceDataPromises = await fetchSourceData(sources);

    const processedSources = await Promise.all(sourceDataPromises);
    return processedSources.flat().filter((source) => source !== null);
  }, []);
  const fetchAndSetSources = async () => {
    // Only fetch if review?.sources is different from lastLoadedSourcesRef.current
    const sourcesString = JSON.stringify(finalizedReview?.sources || []);
    if (lastLoadedSourcesRef.current === sourcesString) {
      return;
    }
    lastLoadedSourcesRef.current = sourcesString;
    if (finalizedReview?.sources) {
      const processedSources = await processSources(finalizedReview.sources);
      setSourcesData(processedSources);
    }

    if (finalizedReview) {
      // Extract silo information from the review
      let siloValue = null;

      // First try direct review.silos
      if (finalizedReview.silos) {
        siloValue = getSelectedSilo(finalizedReview.silos);
      }
      // Then try review.silo
      else if (finalizedReview.silo) {
        siloValue = getSelectedSilo(finalizedReview.silo);
      }
      // Otherwise use getSiloFromReview helper
      else {
        siloValue = getSiloFromReview();
      }

      // Only set non-empty silo values
      if (
        siloValue &&
        typeof siloValue === "string" &&
        siloValue.trim() !== ""
      ) {
        setCurrentSilo(siloValue);
      }
      // Clear the currentSilo if we didn't find one to ensure no tag is shown
      else {
        setCurrentSilo(null);
      }
    }
  };
  useEffect(() => {
    fetchAndSetSources();
  }, [finalizedReview]);

  const togglePanel = () => {
    setDisplayPanel(!displayPanel);
  };

  const toggleMailPanel = () => {
    setDisplayMailPanel(!displayMailPanel);
  };

  const toggleReportPanel = () => {
    setDisplayReportPanel(!displayReportPanel);
  };

  const toggleEditPanel = (content) => {
    if (!displayEditPanel) {
      const rawMarkdown = getFormattedmessage(streamedAnswer.processedText);
      setEditedAnswer(rawMarkdown);
    } else if (content) {
      const originalRawMarkdown = getFormattedmessage(
        streamedAnswer.processedText
      );
      const hasChanges = content !== originalRawMarkdown;

      // Parse the markdown content before updating states
      const parsedContent = marked.parse(content);
      setStreamedAnswer({
        processedText: parsedContent,
        usedSourceIndices: new Set(),
      });
      setMessage(parsedContent);
      if (hasChanges) {
        toast.success("Modifications enregistrées !", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    }
    setDisplayEditPanel(!displayEditPanel);
  };

  const activeSources = useCallback((sources) => {
    if (!sources || !Array.isArray(sources)) {
      return [];
    }

    var filtered = [];

    sources.forEach((source) => {
      if (source?.type === "ARTICLE" && source?.data?.[0]) {
        const authorData = source.data[0].author;

        if (authorData) {
          if (Array.isArray(authorData)) {
            authorData.forEach((author) => {
              if (author && author.enableAvatar) {
                filtered.push({ active: "author", author });
              }
            });
          } else if (authorData.enableAvatar) {
            filtered.push({ active: "author", author: authorData });
          }
        }
        if (source.data[0].chains && source.data[0].chains.length > 0) {
          filtered.push({ active: "chain", chain: source.data[0].chains });
        }
      }
    });

    const uniqueFiltered = filtered.filter(
      (value, index, self) =>
        index ===
        self.findIndex(
          (t) =>
            (t.active === "author" && t.author.id === value.author?.id) ||
            (t.active === "chain" &&
              JSON.stringify(t.chain) === JSON.stringify(value.chain))
        )
    );

    uniqueFiltered.sort((a, b) => {
      if (a.active === "author" && b.active === "chain") {
        return -1; // Authors come before chains
      } else if (a.active === "chain" && b.active === "author") {
        return 1; // Chains come after authors
      }
      return 0; // Keep the order for the same type
    });

    return uniqueFiltered;
  }, []);

  const ITAAFilter = useCallback(
    (sourceData, user) => {
      if (!sourceData || !user) return [];

      return sourceData.filter((source) => {
        if (source.type === "web") {
          return false; // Exclude web sources from the main card display
        }
        if (source.type === "PDF" && source.royalty === "free") {
          if (source.source.includes("ITAA")) {
            return (
              user.institute === "ITAA" &&
              user.numeroAgreation !== "" &&
              user.numeroAgreation !== null
            );
          }
        }
        return true; // Include other sources
      });
    },
    [user]
  );

  const sendReviewData = useCallback(
    async (user, question, answerData, sources) => {
      try {
        let processedSources = null;
        if (sources && Array.isArray(sources) && sources.length > 0) {
          processedSources = removeDuplicates(sources, "rubric");
          processedSources = removeDuplicates(processedSources, "id");

          if (processedSources.length > 0) {
            const sourceDataPromises = await fetchSourceData(processedSources);

            const fetchedSourcesData = await Promise.all(sourceDataPromises);
            const validFetchedSources = fetchedSourcesData
              .flat()
              .filter((source) => source !== null);

            if (validFetchedSources.length > 0) {
              processedSources = validFetchedSources;
              setSourcesData(processedSources);
            } else if (
              fetchedSourcesData.length > 0 &&
              validFetchedSources.length === 0
            ) {
              console.warn("All source fetches failed. Using raw sources.");
              setSourcesData(processedSources);
            } else {
              setSourcesData(processedSources);
            }
          } else {
            processedSources = null;
          }
        }

        const origin = window.location.origin.includes("oeccbb")
          ? "oeccbb"
          : window.location.origin.includes("unitedassociates")
          ? "unitedassociates"
          : "dev";

        if (answerData?.data) {
          // Format silos array for the backend
          let silosToSend = null;
          // Use lockedSilos as it represents the silos at the time of question submission
          if (
            lockedSilos &&
            Array.isArray(lockedSilos) &&
            lockedSilos.length > 0
          ) {
            silosToSend = lockedSilos
              .map((silo) => {
                // Construct the Silo object as per the backend model
                // Ensure all properties are taken from the silo object itself,
                // especially name and label. created_at and updated_at might come from silo.collection
                const name =
                  (typeof silo === "string" ? silo : silo.name) || null;
                const label =
                  typeof silo === "object" && silo.label ? silo.label : ""; // Default to empty string if no label

                let createdAt = null;
                let updatedAt = null;

                if (typeof silo === "object" && silo.collection) {
                  // Attempt to get dates from the collection object if they exist
                  createdAt =
                    silo.collection.created_at || new Date().toISOString(); // Fallback to now if not present
                  updatedAt =
                    silo.collection.updated_at || new Date().toISOString(); // Fallback to now if not present
                } else {
                  // Fallback for older string-based silos or if collection is missing
                  createdAt = new Date().toISOString();
                  updatedAt = new Date().toISOString();
                }

                return {
                  name: name
                    ? name.toString().replace(/^[\[\]"\']|[\[\]"\']$/g, "")
                    : null,
                  label: label.toString().replace(/^[\[\]"\']|[\[\]"\']$/g, ""),
                  created_at: createdAt,
                  updated_at: updatedAt,
                };
              })
              .filter((s) => s.name !== null); // Ensure we only send silos with a name
          }

          // Ensure currentSilo has no square brackets (legacy, may not be needed if silosToSend is primary)
          const currentSiloValue = currentSilo
            ? currentSilo.toString().replace(/^\[|\]$/g, "")
            : null;

          // Build the request body, always including silo information for new questions
          const requestBody = {
            user_id: user.id,
            question,
            answer: answerData.data.body.answer,
            sources:
              processedSources && processedSources.length > 0
                ? processedSources
                : null,
            origin,
            model,
            cost: answerData.data.body.cost || 0,
            provider: answerData.data.body.provider || aiProvider || "tamtam",
            should_render_sources: shouldRenderSourcesRef.current, // Include the flag
          };

          console.log("requestBody--", requestBody);

          // Include silo information for all new questions
          if (currentSiloValue) {
            requestBody.silo = currentSiloValue; // This might be a legacy field now
          }
          if (silosToSend && silosToSend.length > 0) {
            requestBody.silos = silosToSend; // Send the array of Silo objects
          }

          const response = await fetch(`${AI_BASE_URL}/reviews`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          });

          if (response.ok) {
            const result = await response.json();
            console.log("result--", result);
            setRecord(result);
            setMustRate(true);
            setInputDisabled(true);
            onRatingComplete(result);
            onReviewCreated();

            if (conversation) {
              await fetch(
                `${AI_BASE_URL}/conversations/${conversation}/reviews/${result.id}`,
                {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );
              await fetch(`${AI_BASE_URL}/conversations/${conversation}`, {
                method: "PATCH",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              });
              onReviewCreated();
            }
          } else {
            console.error("Error creating review:", await response.json());
          }
        }
      } catch (error) {
        console.error("Error in sendReviewData:", error);
      }
    },
    [
      token,
      onReviewCreated,
      API_BASE_URL,
      fetchSourceData,
      model,
      removeDuplicates,
    ] // Added missing dependencies, kept shouldRenderSources as per user request
  );

  // Add a scroll event listener to detect manual scrolling
  useEffect(() => {
    const handleScroll = () => {
      // Increment the scroll activity counter to track user engagement
      scrollActivityRef.current += 1;

      // Get current scroll position and document metrics
      const currentScrollPosition = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const scrolledToBottom =
        currentScrollPosition + windowHeight >= documentHeight - 100;
      const scrolledUpSignificantly =
        lastScrollPositionRef.current - currentScrollPosition > 100;

      // If streaming is active
      if (isStreamingRef.current) {
        // Case 1: User has actively scrolled up a significant amount
        if (scrolledUpSignificantly) {
          setUserScrolling(true);

          // Clear any pending auto-scroll resumption
          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = null;
          }
        }
        // Case 2: User has manually scrolled to bottom
        else if (scrolledToBottom && userScrolling) {
          // Resume auto-scrolling
          scrollTimeoutRef.current = setTimeout(() => {
            setUserScrolling(false);
          }, 500);
        }
        // Case 3: User is not at bottom and has shown scroll activity
        else if (!scrolledToBottom && scrollActivityRef.current > 3) {
          // Keep user scrolling mode active (prevents jumps)
          setUserScrolling(true);
        }
      } else {
        // Not streaming - don't force scroll behavior
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
          scrollTimeoutRef.current = null;
        }
      }

      // Update the last scroll position
      lastScrollPositionRef.current = currentScrollPosition;
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [userScrolling]);

  // Add event listener for citation clicks
  useEffect(() => {
    const handleCitationClickEvent = (event) => {
      const target = event.target;

      // Match any inline-citation-card element
      if (target.classList.contains("inline-citation-card")) {
        const type = target.dataset.citationType;
        const id = target.dataset.citationId;
        const title = target.dataset.citationTitle || target.textContent;
        const page = target.dataset.citationPage;
        const url = target.dataset.citationUrl || null; // Convert empty string to null
        const objectKey = target.dataset.citationObjectKey;

        if (type && id) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          handleCitationClick(type, id, title, page, url, objectKey);
        }
      }
    };

    document.addEventListener("click", handleCitationClickEvent, true); // Capture phase

    return () => {
      document.removeEventListener("click", handleCitationClickEvent, true);
    };
  }, [citationPopup]);

  // Memoize scrollToBottom
  const scrollToBottom = useCallback(() => {
    if (!userScrolling) {
      requestAnimationFrame(() => {
        const threshold = 100; // pixels from the bottom
        const currentScroll = window.innerHeight + window.scrollY;
        const fullHeight = document.documentElement.scrollHeight;
        // Scroll only if the user is already near the bottom
        if (fullHeight - currentScroll < threshold) {
          window.scrollTo({
            top: fullHeight,
            behavior: "smooth",
          });
        }
      });
    }
  }, [userScrolling]);

  // Use useEffect for scrolling when streamedAnswer changes
  useEffect(() => {
    if (streamedAnswer.processedText && !loading) {
      // Only auto-scroll if user has not manually scrolled up
      if (!userScrolling) {
        scrollToBottom();
      }
      // Update streaming state
      isStreamingRef.current = true;
    } else if (!loading) {
      // Answer streaming has completed
      isStreamingRef.current = false;
    }
  }, [streamedAnswer, loading, scrollToBottom, userScrolling]);

  const fetchData = useCallback(
    async (questionOverride) => {
      // Use the override question if provided, otherwise use the prop question
      if (!question || finalizedReview) return;

      const currentQuestion = questionOverride || question;
      if (!currentQuestion || chatPosted.current) return; // Add check for chatPosted

      try {
        // At the start of the fetch for a new question, capture the source rendering decision.
        // This ref is used later when sending the review data to the backend.
        shouldRenderSourcesRef.current = true;

        // Lock the current silos when sending the question
        setQuestionSent(true);
        setLockedSilos(silos);

        // Reset sources before starting to stream
        setSources([]);
        setAiProvider("tamtam");
        setUserScrolling(false);
        isStreamingRef.current = true;
        scrollActivityRef.current = 0;
        // Reset prefix processing state for the new fetch
        prefixProcessedRef.current = false;

        const getSiloName = (silo) => {
          if (!silo) return null;
          if (typeof silo === "string") return silo;
          // For object silos, prioritize name, but fall back to id for cases like 'web_search'
          return silo.name || silo.id;
        };
        const siloNames = silos.map(getSiloName).filter(Boolean);

        const requestBody = {
          conversation_id: conversation,
          question: currentQuestion,
          session_id: user.id.toString(),
          model: model,
          custom_prompt: customPrompt,
          silos: siloNames,
          token: token,
          page_offset: PAGE_OFFSET,
        };

        const endpoint = `${AI_BASE_URL}/get_answer_stream`;

        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });
        if (!response.ok) {
          if (response.status === 403) {
            const errorData = await response.json();
            const errorMessage =
              "Vous n'avez pas accès à l'un des silos sélectionnés";
            toast.error(errorMessage);
            setLoading(false);
            // This will effectively "remove" the question component by setting its content to null
            setStreamedAnswer({
              processedText: null,
              usedSourceIndices: new Set(),
            });
            setInputDisabled(false);
            if (handleNewDiscussion) {
              handleNewDiscussion();
            }
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setLoading(false);
        let collectedText = "";
        let finalAnswerData = null;
        let finalSources = [];
        let localWebSources = [];
        let currentProvider = "tamtam";

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            // Final processing
            const { processedText, usedSourceIndices } = addSourceCardStyling(
              collectedText,
              localWebSources,
              finalSources
            );

            // Configure marked to preserve HTML
            marked.setOptions({
              gfm: true,
              breaks: true,
            });
            let finalAnswerHtml = marked.parse(processedText);

            // Apply citation processing AFTER markdown parsing
            const citationProcessed = applyCitationProcessing(
              finalAnswerHtml,
              localWebSources,
              finalSources
            );

            // Remove the unused sources section - we only want inline citations
            setStreamedAnswer({
              processedText: citationProcessed,
              usedSourceIndices,
            });

            setDisplayCards(true);
            setMustRate(true);
            setInputDisabled(true);
            setFinalSources(finalSources);
            // Mark streaming as complete
            isStreamingRef.current = false;

            if (finalAnswerData) {
              finalAnswerData.data.body.provider = currentProvider;
            }

            if (!reviewSent && finalAnswerData) {
              await sendReviewData(
                user,
                currentQuestion,
                finalAnswerData,
                finalSources || []
              );
              setReviewSent(true);
              chatPosted.current = true;
            }
            break;
          }

          const chunk = decoder.decode(value);
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                switch (data.type) {
                  case "token": {
                    let updateDisplayWith = null;
                    // Logic for standard streaming answer
                    let newChunk = data.data;
                    let currentEffectiveText = collectedText;

                    if (!prefixProcessedRef.current) {
                      const potentialCombined = currentEffectiveText + newChunk;
                      if (potentialCombined.startsWith("[CONTEXTUAL_ANSWER]")) {
                        shouldRenderSourcesRef.current = true;
                        currentEffectiveText = potentialCombined.substring(
                          "[CONTEXTUAL_ANSWER]".length
                        );
                        prefixProcessedRef.current = true;
                        updateDisplayWith = currentEffectiveText;
                      } else if (
                        potentialCombined.startsWith("[GENERAL_ANSWER]")
                      ) {
                        shouldRenderSourcesRef.current = false;
                        currentEffectiveText = potentialCombined.substring(
                          "[GENERAL_ANSWER]".length
                        );
                        prefixProcessedRef.current = true;
                        updateDisplayWith = currentEffectiveText;
                      } else {
                        const contextualPrefix = "[CONTEXTUAL_ANSWER]";
                        const generalPrefix = "[GENERAL_ANSWER]";
                        if (
                          (contextualPrefix.startsWith(potentialCombined) &&
                            potentialCombined.length <
                              contextualPrefix.length) ||
                          (generalPrefix.startsWith(potentialCombined) &&
                            potentialCombined.length < generalPrefix.length)
                        ) {
                          currentEffectiveText = potentialCombined;
                        } else {
                          currentEffectiveText = potentialCombined;
                          updateDisplayWith = currentEffectiveText;
                          prefixProcessedRef.current = true;
                        }
                      }
                    } else {
                      currentEffectiveText += newChunk;
                      updateDisplayWith = currentEffectiveText;
                    }
                    collectedText = currentEffectiveText;

                    // Unified update logic for both web search and streaming
                    if (updateDisplayWith !== null) {
                      const textWithCards = addSourceCardStyling(
                        updateDisplayWith,
                        localWebSources,
                        finalSources
                      );
                      const parsedHtml = marked.parse(
                        textWithCards.processedText
                      );

                      // Apply citation processing AFTER markdown parsing
                      const citationProcessed = applyCitationProcessing(
                        parsedHtml,
                        localWebSources,
                        finalSources
                      );

                      setStreamedAnswer({
                        processedText: citationProcessed,
                        usedSourceIndices: textWithCards.usedSourceIndices,
                      });
                    }

                    // Update other states with the final collectedText (raw markdown)
                    setMessage(getFormattedAnswer(collectedText));

                    const { processedText: finalProcessedText } =
                      addSourceCardStyling(
                        collectedText,
                        localWebSources,
                        finalSources
                      );

                    finalAnswerData = {
                      data: {
                        body: {
                          answer: finalProcessedText, // Use processed text with citations
                          cost: data.cost || 0,
                          provider: currentProvider,
                        },
                      },
                    };
                    setAnswerData({
                      answered: true,
                      content: finalProcessedText,
                    });
                    break;
                  }
                  case "buttons":
                    if (data.data) {
                      setVersionButtons(data.data);
                    }
                    break;
                  case "source":
                    // Handle individual source data
                    if (data.data) {
                      if (data.data.type === "web") {
                        if (
                          !localWebSources.some(
                            (ws) => ws.url === data.data.url
                          )
                        ) {
                          localWebSources.push(data.data);
                        }
                      }
                      // Add the individual source to our sources array
                      setSources((prevSources) => {
                        const newSources = [...(prevSources || []), data.data];
                        finalSources = newSources;
                        return newSources;
                      });
                    }
                    break;
                  case "provider":
                    // Handle provider information
                    if (data.data) {
                      currentProvider = data.data;
                      setAiProvider(data.data);
                      if (finalAnswerData) {
                        finalAnswerData.data.body.provider = data.data;
                      }
                    }
                    break;
                  case "silo":
                    // Handle silo information - ONLY update if this is a new question
                    if (!finalizedReview) {
                      if (data.data && data.data.name) {
                        setCurrentSilo(data.data.name);
                      } else if (data.data && typeof data.data === "string") {
                        // If the server sends the silo name directly as a string
                        setCurrentSilo(data.data);
                      } else if (data.data) {
                        // Try to find a name property in the data object
                        const siloName =
                          data.data.name ||
                          data.data.id ||
                          data.data.title ||
                          JSON.stringify(data.data);
                        setCurrentSilo(siloName);
                      }
                    }
                    break;
                  case "error":
                    // console.error("Error from server:", data.data);
                    setAnswerData({ answered: false });
                    break;
                  case "record":
                    // When we get the record, set it and enable the review functionality
                    if (data.data) {
                      // If we have silos data in the response, make sure we have it in the record
                      if (
                        !data.data.silos &&
                        silos &&
                        Array.isArray(silos) &&
                        silos.length > 0
                      ) {
                        data.data.silos = silos
                          .map((silo) => {
                            return typeof silo === "string"
                              ? silo
                              : silo.name || silo.id || null;
                          })
                          .filter(Boolean);
                      }
                      setRecord(data.data);
                      onRatingComplete(data.data);
                    }
                    break;
                }
              } catch (e) {
                console.error("Error parsing SSE data:", e);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
        setAnswerData({ answered: false });
      }
    },
    [
      question,
      user?.id,
      model,
      silos,
      conversation,
      customPrompt,
      token,
      finalizedReview, // Add review to dependencies
      sendReviewData, // Add sendReviewData
    ]
  );

  useEffect(() => {
    // This effect should only trigger for new questions that don't have a review yet.
    // Once a review is present, this question is considered "final" and should not be re-fetched.
    if (!finalizedReview && !questionSent) {
      fetchData();
    }
  }, [finalizedReview, fetchData, questionSent]);

  // Add a function to determine which icon to show
  const shouldShowOpenAI = () => {
    // If we haven't received the answer yet, show IconEllipse
    if (loading || !displayCards) {
      return false;
    }

    // After receiving the answer, check if we have any valid sources
    return ITAAFilter(sourcesData, user).length === 0;
  };

  // Create separate components for Authors and Sources sections with proper memo implementation
  const AuthorsSection = memo(({ sourcesData, activeSources }) => {
    return (
      <>
        {activeSources(sourcesData)
          .filter((source) => source && source.active === "author")
          .map((source, index) => (
            <AuthorCard
              key={`author-${source.author.id || index}`}
              author={source.author}
            />
          ))}
      </>
    );
  });

  const getUserEventAccess = (sources) => {
    const videoEvents = [
      ...new Set(
        sources
          .filter((item) => item.type === "VIDEO" && item.event !== undefined)
          .map((item) => item.event)
      ),
    ];
    if (videoEvents.length > 0) {
      getUserRegistration(user?.id, videoEvents, token)
        .then((result) => {
          setUserEventsAccess(result);
        })
        .catch((error) => {
          console.error("error", error);
        });
    }
  };

  const SourcesSection = memo(
    ({ sourcesData, user, displayCards, togglePanel }) => {
      const [invalidSourceKeys, setInvalidSourceKeys] = useState(new Set());

      const handleSourceError = useCallback((uniqueKey) => {
        setInvalidSourceKeys((prev) => {
          const next = new Set(prev);
          next.add(uniqueKey);
          return next;
        });
      }, []);

      // 1. Enrich sources with their original index to handle duplicates and stable key generation
      const enrichedSources = useMemo(() => {
        const filtered = ITAAFilter(sourcesData, user);
        return filtered.map((source, index) => ({
          ...source,
          _originalIndex: index,
        }));
      }, [sourcesData, user]);

      // 2. Filter out invalid sources using the stable _originalIndex
      const validSources = useMemo(() => {
        return enrichedSources.filter((sourceData) => {
             const uniqueKey = `source-${sourceData.type}-${
              sourceData.type === "PDF"
                ? `${sourceData.source}-page-${sourceData.page}-${sourceData._originalIndex}`
                : sourceData.type === "RUBRIC"
                ? `rubric-${sourceData.rubric || sourceData._originalIndex}`
                : sourceData.type === "VIDEO"
                ? `video-${sourceData.video_url || sourceData._originalIndex}`
                : sourceData.type === "web"
                ? `web-${sourceData.id || sourceData._originalIndex}`
                : `article-${sourceData.id || sourceData._originalIndex}`
            }`;
            return !invalidSourceKeys.has(uniqueKey);
        });
      }, [enrichedSources, invalidSourceKeys]);

      const displayableSources = useMemo(() => {
         return validSources.slice(0, 4);
      }, [validSources]);

      return (
        <div className="sources-cards">
          {displayableSources.map((sourceData, index) => {
            // Use the SAME key generation logic, relying on _originalIndex
            const uniqueKey = `source-${sourceData.type}-${
              sourceData.type === "PDF"
                ? `${sourceData.source}-page-${sourceData.page}-${sourceData._originalIndex}`
                : sourceData.type === "RUBRIC"
                ? `rubric-${sourceData.rubric || sourceData._originalIndex}`
                : sourceData.type === "VIDEO"
                ? `video-${sourceData.video_url || sourceData._originalIndex}`
                : sourceData.type === "web"
                ? `web-${sourceData.id || sourceData._originalIndex}`
                : `article-${sourceData.id || sourceData._originalIndex}`
            }`;

            return (
              <React.Fragment key={uniqueKey}>
                {index < 3 && displayCards && (
                  <>
                    {sourceData.type === "RUBRIC" ? (
                      sourceData.data?.[0] && (
                        <ContentCard
                          user={user}
                          type={sourceData.type}
                          question={sourceData.data[0].titleFr}
                          publishDate={
                            sourceData.data[0].questions?.[0]?.updatedAt
                              ?.date ||
                            sourceData.data[0].questions?.[0]?.createdAt?.date
                          }
                          chain={sourceData.data[0].chain}
                          author={sourceData.data[0].author}
                          rubric={sourceData.data[0]}
                          similarity={sourceData.similarity}
                          recency={sourceData.recency}
                          score={sourceData.score}
                          token={token}
                          onError={() => handleSourceError(uniqueKey)}
                        />
                      )
                    ) : sourceData.type === "ARTICLE" ? (
                      sourceData.data?.[0] && (
                        <ContentCard
                          user={user}
                          type={sourceData.type}
                          question={sourceData.data[0].title}
                          publishDate={
                            sourceData.data[0].updatedAt ||
                            sourceData.data[0].publishedAt
                          }
                          chain={sourceData.data[0].chains}
                          author={sourceData.data[0].author}
                          id={sourceData.data[0].id}
                          similarity={sourceData.similarity}
                          recency={sourceData.recency}
                          score={sourceData.score}
                          token={token}
                          onError={() => handleSourceError(uniqueKey)}
                        />
                      )
                    ) : sourceData.type === "VIDEO" ? (
                      sourceData && (
                        <ContentCard
                          type={sourceData.type}
                          url={sourceData.video_url}
                          startTime={sourceData.start_time}
                          endTime={sourceData.end_time}
                          event={sourceData.event}
                          user={user}
                          token={token}
                          userEventsAccess={userEventsAccess}
                          onError={() => handleSourceError(uniqueKey)}
                        />
                      )
                    ) : (
                      <ContentCard
                        user={user}
                        type={sourceData.type}
                        question={formatSourceName(sourceData.source)}
                        page={sourceData.page}
                        target_page={sourceData.target_page}
                        url={sourceData.url || sourceData.link}
                        coverUrl={sourceData.cover_url}
                        similarity={sourceData.similarity}
                        recency={sourceData.recency}
                        score={sourceData.score}
                        name={sourceData.name}
                        extra={sourceData.extra}
                        extra_pages={sourceData.extra_pages}
                        panel={false}
                        token={token}
                        content={sourceData.content}
                        fileObjectKey={
                          sourceData.objectKey || sourceData.object_key
                        }
                        onError={() => handleSourceError(uniqueKey)}
                      />
                    )}
                  </>
                )}

                {index === 3 && displayCards && (
                  <div className="more-cards" onClick={togglePanel}>
                    <p>+ {validSources.length - 3}</p>
                    <p>sources en plus</p>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      );
    }
  );

  // Add useEffect to handle showNote state update
  // useEffect(() => {
  //   if (sources && sources.length > 0) {
  //     const hasPaidMIPP = sources.some(
  //       (source) =>
  //         source.royalty === "paid" &&
  //         source.source.includes("MIPP_New2024_y20_ED_Final")
  //     );
  //     if (hasPaidMIPP) {
  //       setshowNote(true);
  //     }
  //   }
  // }, [sources]);

  const handleRatingSubmit = async (rating) => {
    try {
      if (record) {
        await fetch(`${AI_BASE_URL}/reviews/${record.id}`, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ rating }),
        });

        if (onRatingComplete) {
          onRatingComplete();
        }
      }
    } catch (error) {
      console.error("Error submitting rating:", error);
    }
  };

  const handleSkipRating = () => {
    if (onRatingComplete) {
      onRatingComplete();
    }
  };

  const copyFormattedAnswer = () => {
    if (answerRef.current) {
      const answerText = answerRef.current.innerText;
      navigator.clipboard.writeText(answerText);
      toast.success("Réponse copiée !", {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: true,
      });
    }
  };

  const copyEmailContent = async () => {
    try {
        const rawBody = finalizedReview?.answer || answerData?.content || message || "";
        const parsedHtml = marked.parse(rawBody);
        const styledHtml = injectMailStyles(parsedHtml);
        const plainTextBody = convertHtmlToPlainText(parsedHtml);

        if (navigator.clipboard && navigator.clipboard.write) {
            const textBlob = new Blob([plainTextBody], { type: "text/plain" });
            const htmlBlob = new Blob([styledHtml], { type: "text/html" });
            
            await navigator.clipboard.write([
                new ClipboardItem({ "text/plain": textBlob, "text/html": htmlBlob }),
            ]);
            
             toast.success("Contenu copié (Formaté) !", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: true,
            });
        } else {
             // Fallback
             await navigator.clipboard.writeText(plainTextBody);
             toast.success("Contenu copié (Texte brut).", {
                position: "top-right",
                autoClose: 3000,
            });
        }
    } catch (err) {
        console.error("Copy failed", err);
        toast.error("Erreur lors de la copie.");
    }
  };


  // Function to determine what silo to display in the UI
  const getSiloDisplay = () => {
    // If we shouldn't show silo tags for this review, return null
    if (!shouldShowSiloTags) {
      return null;
    }

    // For existing reviews, always use review.silo or review.silos
    if (finalizedReview) {
      if (finalizedReview.silo) {
        return getSelectedSilo(finalizedReview.silo);
      }
      if (finalizedReview.silos) {
        return getSelectedSilo(finalizedReview.silos);
      }
      return null;
    }

    // For new questions (no review), use the currently selected silo
    if (silos && Array.isArray(silos) && silos.length > 0) {
      const selectedSilo = getSelectedSilo(silos);
      if (selectedSilo) {
        return selectedSilo;
      }
    }

    return null;
  };

  const countActiveResources = () => {
    if (!shouldShowSiloTags) {
      return 0;
    }

    let count = 0;

    if (!finalizedReview && !questionSent && silos && Array.isArray(silos)) {
      silos.forEach((silo) => {
        const siloName = getSelectedSilo(silo);
        if (
          siloName &&
          typeof siloName === "string" &&
          siloName.trim() !== ""
        ) {
          const siloNames = splitMultipleSilos(siloName);
          count += siloNames.length;
        }
      });
    }

    if (
      !finalizedReview &&
      questionSent &&
      lockedSilos &&
      Array.isArray(lockedSilos)
    ) {
      lockedSilos.forEach((silo) => {
        const siloName = getSelectedSilo(silo);
        if (
          siloName &&
          typeof siloName === "string" &&
          siloName.trim() !== ""
        ) {
          const siloNames = splitMultipleSilos(siloName);
          count += siloNames.length;
        }
      });
    }

    if (finalizedReview) {
      if (
        finalizedReview.silos &&
        Array.isArray(finalizedReview.silos) &&
        finalizedReview.silos.length > 0
      ) {
        finalizedReview.silos.forEach((silo) => {
          const isFullObject =
            silo &&
            typeof silo === "object" &&
            (silo.title ||
              silo.label ||
              silo.coverUrl ||
              silo.coverImageUrl ||
              silo.authors);

          if (isFullObject) {
            count += 1;
          } else {
            const siloName = getSelectedSilo(silo);
            if (
              siloName &&
              typeof siloName === "string" &&
              siloName.trim() !== ""
            ) {
              const siloNames = splitMultipleSilos(siloName);
              count += siloNames.length;
            } else if (typeof silo === "object" && silo.name) {
              count += 1;
            }
          }
        });
      } else if (
        currentSilo &&
        typeof currentSilo === "string" &&
        currentSilo.trim() !== ""
      ) {
        const siloNames = splitMultipleSilos(currentSilo);
        count += siloNames.length;
      }
    }

    return count;
  };

  const getActiveSiloObjects = () => {
    if (!shouldShowSiloTags) {
      return [];
    }

    const activeSiloNames = new Set();
    const activeSiloObjects = [];

    if (!finalizedReview && !questionSent && silos && Array.isArray(silos)) {
      silos.forEach((silo) => {
        const siloName = getSelectedSilo(silo);
        if (
          siloName &&
          typeof siloName === "string" &&
          siloName.trim() !== ""
        ) {
          const siloNames = splitMultipleSilos(siloName);
          siloNames.forEach((name) => {
            if (!activeSiloNames.has(name)) {
              activeSiloNames.add(name);
              const siloObj = userSilots.find(
                (s) => (s.name || s) === name || (s.label || s) === name
              );
              if (siloObj) {
                activeSiloObjects.push(siloObj);
              }
            }
          });
        }
      });
    }

    if (
      !finalizedReview &&
      questionSent &&
      lockedSilos &&
      Array.isArray(lockedSilos)
    ) {
      lockedSilos.forEach((silo) => {
        const siloName = getSelectedSilo(silo);
        if (
          siloName &&
          typeof siloName === "string" &&
          siloName.trim() !== ""
        ) {
          const siloNames = splitMultipleSilos(siloName);
          siloNames.forEach((name) => {
            if (!activeSiloNames.has(name)) {
              activeSiloNames.add(name);
              const siloObj = userSilots.find(
                (s) => (s.name || s) === name || (s.label || s) === name
              );
              if (siloObj) {
                activeSiloObjects.push(siloObj);
              }
            }
          });
        }
      });
    }

    if (finalizedReview) {
      if (
        finalizedReview.silos &&
        Array.isArray(finalizedReview.silos) &&
        finalizedReview.silos.length > 0
      ) {
        finalizedReview.silos.forEach((silo) => {
          const isFullObject =
            silo &&
            typeof silo === "object" &&
            (silo.title ||
              silo.label ||
              silo.coverUrl ||
              silo.coverImageUrl ||
              silo.authors);

          if (isFullObject) {
            const siloName = silo.name || silo.label || getSelectedSilo(silo);
            const nameKey = siloName ? String(siloName).trim() : null;
            if (nameKey && !activeSiloNames.has(nameKey)) {
              activeSiloNames.add(nameKey);

              const siloObj = userSilots.find((s) => {
                const sName = String(s.name || s).trim();
                const sLabel = String(s.label || s).trim();
                return sName === nameKey || sLabel === nameKey;
              });

              if (siloObj) {
                activeSiloObjects.push(siloObj);
              } else {
                activeSiloObjects.push(silo);
              }
            }
          } else {
            const siloName = getSelectedSilo(silo);
            if (
              siloName &&
              typeof siloName === "string" &&
              siloName.trim() !== ""
            ) {
              const siloNames = splitMultipleSilos(siloName);
              siloNames.forEach((name) => {
                const nameKey = name.trim();
                if (nameKey && !activeSiloNames.has(nameKey)) {
                  activeSiloNames.add(nameKey);
                  const siloObj = userSilots.find((s) => {
                    const sName = String(s.name || s).trim();
                    const sLabel = String(s.label || s).trim();
                    return sName === nameKey || sLabel === nameKey;
                  });
                  if (siloObj) {
                    activeSiloObjects.push(siloObj);
                  } else if (typeof silo === "object" && silo.name) {
                    activeSiloObjects.push(silo);
                  }
                }
              });
            } else if (typeof silo === "object" && silo.name) {
              const nameKey = String(silo.name).trim();
              if (nameKey && !activeSiloNames.has(nameKey)) {
                activeSiloNames.add(nameKey);
                activeSiloObjects.push(silo);
              }
            }
          }
        });
      } else if (
        currentSilo &&
        typeof currentSilo === "string" &&
        currentSilo.trim() !== ""
      ) {
        const siloNames = splitMultipleSilos(currentSilo);
        siloNames.forEach((name) => {
          if (!activeSiloNames.has(name)) {
            activeSiloNames.add(name);
            const siloObj = userSilots.find(
              (s) => (s.name || s) === name || (s.label || s) === name
            );
            if (siloObj) {
              activeSiloObjects.push(siloObj);
            }
          }
        });
      }
    }

    return activeSiloObjects;
  };

  const DisplaySiloCard = ({ res }) => {
    const title = res.title || res.label || res.name || "Silo";
    const authorsList = res.authors || [];
    const authors = authorsList.length
      ? authorsList
          .slice(0, 2)
          .map((a) => `${a.firstName} ${a.lastName}`)
          .join(", ") + (authorsList.length > 2 ? "..." : "")
      : null;
    const coverUrl = res.coverUrl || res.coverImageUrl || null;

    return (
      <div
        className={`silo-item ${
          res.expired ? "state-expired" : "state-selected"
        }`}
      >
        <div className="silo-thumbnail">
          {coverUrl ? (
            <img src={coverUrl} alt={title} />
          ) : (
            <div className="thumbnail-placeholder">📘</div>
          )}
        </div>
        {res.organization?.avatarUrl && (
          <img
            src={res.organization.avatarUrl}
            className="organization-logo"
            alt={res.organization.name}
          />
        )}
        <div className="silo-details">
          <h3 className="silo-title">{title}</h3>
          {authors && <p className="silo-authors">{authors}</p>}
        </div>
      </div>
    );
  };

  const CloseIcon = ({ className, ...props }) => (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );

  if (streamedAnswer.processedText === null && !loading) {
    return null;
  }

  return (
    <div className="user-question-container" ref={containerRef}>
      <div className="user-question">
        {avatar && <img src={avatar} className="avatar" alt="user" />}
        <h2>Vous : </h2>
      </div>
      <p className="bordered">
        <span className="question-text">{question}</span>

        {shouldShowSiloTags &&
          (() => {
            const resourceCount = countActiveResources();
            if (resourceCount === 0) return null;
            return (
              <span
                className="silo-tag"
                onClick={() => setShowActiveSilosModal(true)}
                style={{ cursor: "pointer" }}
              >
                {resourceCount}{" "}
                {resourceCount === 1 ? "ressource activé" : "ressources activé"}
              </span>
            );
          })()}
      </p>
      <div className="border-bottom"></div>
      <div className="ai-response">
        {aiProvider === "openai" ? (
          <>
            <IconOpenAI />
            <h2>Chatgpt : </h2>
          </>
        ) : aiProvider === "perplexity" ? (
          <>
            <IconPerplexity />
            <h2>Perplexity : </h2>
          </>
        ) : (
          <>
            <IconEllipse />
            <h2>Support AI : </h2>
          </>
        )}
      </div>

      {/* Tooltip for silo expiration */}
      {tagTooltip && (
        <div
          style={{
            position: "fixed",
            top: `${tooltipPosition.y}px`,
            left: `${tooltipPosition.x}px`,
            transform: "translate(-50%, -100%)",
            backgroundColor:
              tagTooltip.daysRemaining <= 7
                ? "#d32f2f" // Red for < 7 days
                : tagTooltip.daysRemaining <= 30
                ? "#ff9800" // Orange for < 30 days
                : "#1976d2", // Default blue
            color: "white",
            padding: "5px 10px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: "bold",
            zIndex: 9999,
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            whiteSpace: "nowrap",
          }}
        >
          {tagTooltip.daysRemaining
            ? `Expire dans ${tagTooltip.daysRemaining} ${
                tagTooltip.daysRemaining === 1 ? "jour" : "jours"
              }`
            : `Expire le ${formatDate(tagTooltip.expireAt)}`}
          <div
            style={{
              position: "absolute",
              bottom: "-6px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: `6px solid ${
                tagTooltip.daysRemaining <= 7
                  ? "#d32f2f" // Red for < 7 days
                  : tagTooltip.daysRemaining <= 30
                  ? "#ff9800" // Orange for < 30 days
                  : "#1976d2" // Default blue
              }`,
            }}
          />
        </div>
      )}

      <div className="answer" ref={answerRef}>
        {loading ? (
          <Skeleton count={8} containerClassName="skeleton-answer" />
        ) : (
          <>
            <div
              dangerouslySetInnerHTML={{ __html: streamedAnswer.processedText }}
              className="stream-text"
            />
            {displayCards && showNote && (
              <p className="note">
                <b>Note : </b> Cette réponse provient du manuel IPP 2024. Pour
                utiliser ses données dans le chatbot, vous pouvez l'acheter à{" "}
                <a
                  href="https://taxandmanagement.be/produit/approche-theorique-et-pratique-de-limpot-des-personnes-physiques-2024"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Manuel IPP
                </a>
              </p>
            )}

            {!loading && displayCards && streamedAnswer.processedText && (
              <>
                {versionButtons.length > 0 && (
                  <div className="version-buttons">
                    {versionButtons.map((button, index) => (
                      <button
                        key={index}
                        className="version-button"
                        onClick={() => {
                          if (button.value) {
                            // Reset state variables for new question
                            setStreamedAnswer({
                              processedText: "",
                              usedSourceIndices: new Set(),
                            });
                            setSourcesData([]);
                            setSources([]);
                            setVersionButtons([]);
                            setDisplayCards(false);
                            setLoading(true);
                            setAiProvider("tamtam"); // Reset to default
                            setUserScrolling(false); // Reset user scrolling state
                            isStreamingRef.current = true; // Mark as streaming
                            scrollActivityRef.current = 0; // Reset scroll counter
                            chatPosted.current = false; // Reset the flag for new question
                            setCurrentSilo(silos?.[0]?.name || null); // Reset to default silo

                            // Call fetchData with the button's value as the question
                            fetchData(button.value);
                          }
                        }}
                      >
                        {button.text}
                      </button>
                    ))}
                  </div>
                )}
                <div className="action-buttons">
                  <button className="action-button" onClick={toggleEditPanel}>
                    <IconEdit />
                  </button>
                  <button className="action-button" onClick={toggleMailPanel}>
                    <IconSendMail />
                  </button>
                  <button
                    className="action-button"
                    onClick={copyFormattedAnswer}
                  >
                    <IconCopy />
                  </button>
                  <button className="action-button" onClick={toggleReportPanel}>
                    <IconReport />
                  </button>
                </div>
                <div className="border-bottom"></div>
              </>
            )}
          </>
        )}
      </div>

      {
        <>
          {/* Authors */}
          {!loading &&
            displayCards &&
            Array.isArray(sourcesData) &&
            activeSources(sourcesData).length > 0 && (
              <>
                <div className="sources">
                  <IconSource />
                  <h2>Auteurs et chaines : </h2>
                </div>
              </>
            )}

          {loading ? (
            <>
              <Skeleton
                containerClassName="skeleton-author-container"
                className="skeleton-active-sources"
                count={3}
              />
              <Skeleton
                containerClassName="skeleton-container"
                className="skeleton-card"
                count={4}
              />
            </>
          ) : (
            <div className="active-author-container">
              {displayCards && Array.isArray(sourcesData) && (
                <>
                  {/* Authors Section */}
                  <AuthorsSection
                    sourcesData={sourcesData}
                    activeSources={activeSources}
                  />

                  {/* Separator */}
                  {Array.isArray(sourcesData) &&
                    activeSources(sourcesData).filter(
                      (source) => source.active === "chain"
                    ).length > 0 &&
                    activeSources(sourcesData).filter(
                      (source) => source.active === "author"
                    ).length > 0 && <div className="separator"></div>}

                  {/* Chains Section */}
                  {activeSources(sourcesData)
                    .filter((source) => source && source.active === "chain")
                    .map((source, index) => (
                      <div
                        className="active-author"
                        key={`chain-${index}`}
                        onClick={() =>
                          window.open(
                            `https://blog.tamtam.pro/fr/chain/${transformString(
                              source.chain[0].nameFr
                            )}/${source.chain[0].id}`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        <img
                          src={source.chain[0].mediaChain[0].avatarUrl}
                          alt=""
                        />
                        <h3>{source.chain[0].nameFr}</h3>
                      </div>
                    ))}
                </>
              )}

              {/* Sources */}
              {!loading &&
                displayCards &&
                ITAAFilter(getAvailableSources(), user).length > 0 && (
                  <>
                    <div className="sources">
                      <IconSource />
                      <h2>Sources : </h2>
                    </div>
                  </>
                )}

              {!loading && displayCards && (
                <>
                  <SourcesSection
                    sourcesData={getAvailableSources()}
                    user={user}
                    displayCards={displayCards}
                    togglePanel={togglePanel}
                  />
                </>
              )}
            </div>
          )}
        </>
      }

      {displayReportPanel && (
        <ReportPanel
          show={displayReportPanel}
          togglePanel={() => setDisplayReportPanel(!displayReportPanel)}
          userId={user.id}
          token={token}
          reviewId={record?.id}
          email={user.email}
        />
      )}

      {displayMailPanel && (
        <>
          <div className="mail-panel-overlay" onClick={togglePanel}>
            <div
              className="mail-panel open"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mail-panel-header">
                <div className="mail-panel-title">
                  <h3>Envoyer par mail</h3>
                  <div className="mail-input">
                    <label htmlFor="mail" className="mail-label">
                      Destinataire :
                    </label>
                    <input
                      type="email"
                      id="mail"
                      name="mail"
                      placeholder=""
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                    />
                  </div>
                </div>
                <div className="mail-panel-close" onClick={toggleMailPanel}>
                  <IconClose />
                </div>
              </div>
              <div className="mail-panel-content">
                <div className="mail-panel-cards">
                  <div className="mail-input">
                    <label htmlFor="object" className="mail-label">
                      Objet :
                    </label>
                    <textarea
                      name="object"
                      rows={3}
                      id="object"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </div>
                  <div className="mail-input">
                    <label htmlFor="message" className="mail-label">
                      Message :
                    </label>
                    <div
                      ref={editorRef}
                      className="mail-message-editor"
                      contentEditable
                      style={{
                        flex: 1,
                        width: "100%",
                        minHeight: "200px",
                        maxHeight: "400px",
                        overflowY: "auto",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "10px",
                        backgroundColor: "#fff",
                        color: "#000",
                        whiteSpace: "pre-wrap"
                      }}
                      onFocus={() => { isEditingRef.current = true; }}
                      onBlur={() => { isEditingRef.current = false; }}
                      onInput={(e) => {
                        const markdown = htmlToMarkdown(e.currentTarget.innerHTML);
                        setMessage(markdown);
                      }}
                    />
                  </div>
                </div>
                <div className="mail-panel-buttons">
                  <a
                    className="mail-send-button"
                    style={{ cursor: "pointer" }}
                    onClick={async (e) => {
                      e.preventDefault();
                      if (!recipient) {
                        toast.warn("Veuillez entrer une adresse e-mail.", {
                          position: "top-right",
                          autoClose: 3000,
                          hideProgressBar: true,
                        });
                        return;
                      }

                      // 1. Prepare Content
                      const rawBody = finalizedReview?.answer || answerData?.content || message || "";
                      const parsedHtml = marked.parse(rawBody);
                      const plainTextBody = convertHtmlToPlainText(parsedHtml);
                      
                      // 2. Copy Rich Text to Clipboard (Best Effort)
                      try {
                        if (navigator.clipboard && navigator.clipboard.write) {
                           const styledHtml = injectMailStyles(parsedHtml);
                           const fullContent = plainTextBody;
                           const fullContentHtml = styledHtml;
                           
                           const textBlob = new Blob([fullContent], { type: "text/plain" });
                           const htmlBlob = new Blob([fullContentHtml], { type: "text/html" });
                           
                           await navigator.clipboard.write([
                              new ClipboardItem({ "text/plain": textBlob, "text/html": htmlBlob }),
                           ]);
                           toast.success("Tableau formaté copié ! COLLEZ (Ctrl+V) dans le mail pour le voir.", {
                             autoClose: 5000
                           });
                        }
                      } catch (err) {
                        console.error("Clipboard failed", err);
                      }

                      // 3. Open Mail Client (fallback to placeholder message)
                      // We verify user focus to avoid popup blockers if possible, though inside onClick is usually safe.
                      setTimeout(() => {
                          // Robust Mac detection
                          const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
                          const pasteCmd = isMac ? "Cmd + V" : "Ctrl + V";
                          const selectCmd = isMac ? "Cmd + A" : "Ctrl + A";
                          
                          const instructionMessage = `(Pour voir le texte formaté : ${selectCmd} / ${pasteCmd} maintenant)`;

                          const mailtoLink = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(instructionMessage)}`;
                          window.location.href = mailtoLink;
                      }, 500);

                      // Update the review with the recipient email (Background)
                      if (record && record.id) {
                        try {
                          const recipientList = recipient
                            .split(",")
                            .map((email) => email.trim());

                          fetch(`${AI_BASE_URL}/reviews/${record.id}`, {
                              method: "PATCH",
                              headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                              },
                              body: JSON.stringify({
                                recipient_emails: recipientList,
                              }),
                            }).catch(err => console.error("Update failed", err));
                        } catch (error) {
                          console.error("Error:", error);
                        }
                      }
                    }}
                  >
                    Ouvrir l'application Mail
                  </a>
                  <button
                    type="button"
                    onClick={copyEmailContent}
                    className="mail-copy-button"
                  >
                    Copier le contenu
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {displayEditPanel && (
        <ChatEditor
          isOpen={displayEditPanel}
          onClose={toggleEditPanel}
          content={streamedAnswer.processedText}
          title="Modifier la réponse"
          reviewId={record?.id}
        />
      )}

      {/* Citation popup for inline citations */}
      {citationPopup && (
        <div
          key={`citation-${citationPopup.type}-${citationPopup.id}`}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            display: "flex",
            justifyContent:
              citationPopup.type === "ARTICLE"
                ? "flex-end"
                : citationPopup.type === "PDF"
                ? "flex-end"
                : "center",
            alignItems:
              citationPopup.type === "ARTICLE"
                ? "flex-start"
                : citationPopup.type === "PDF"
                ? "flex-start"
                : "center",
            padding:
              citationPopup.type === "ARTICLE" || citationPopup.type === "PDF"
                ? "10px"
                : "0",
            boxSizing: "border-box",
          }}
        >
          {citationPopup.type === "ARTICLE" && (
            <FrameWrapperArticle
              id={citationPopup.id}
              closePopup={closeCitationPopup}
              token={token}
            />
          )}

          {citationPopup.type === "RUBRIC" && (
            <RubricByIdWrapper
              rubricId={citationPopup.id}
              closePopup={closeCitationPopup}
              token={token}
            />
          )}

          {citationPopup.type === "PDF" && (
            <PdfByIdWrapper
              pdfId={citationPopup.id}
              url={citationPopup.url || ""}
              page={citationPopup.page}
              objectKey={citationPopup.objectKey}
              closePopup={closeCitationPopup}
              token={token}
            />
          )}
        </div>
      )}

      {/* Add the Panel for more sources */}
      {displayPanel && (
        <>
          <div className="cards-panel-overlay" onClick={togglePanel}>
            <div
              className="cards-panel open"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cards-panel-header">
                {/* Left-close button */}
                <div className="cards-panel-close-left">
                  <button className="close-sidebar-btn" onClick={togglePanel}>
                    <IconCloseSidebar />
                  </button>
                </div>

                {/* Centered title */}
                <div className="cards-panel-title centered-title">
                  <h3>
                    {ITAAFilter(getAvailableSources(), user).length} Sources
                  </h3>
                </div>
              </div>

              <div className="cards-panel-content">
                <div className="cards-panel-question-section">
                  <div className="cards-panel-question-text">
                    <p>{question}</p>
                  </div>

                  {/* Separator line */}
                  <div className="border-bottom"></div>
                </div>

                <div className="cards-panel-cards cards-grid">
                  {ITAAFilter(getAvailableSources(), user).map(
                    (sourceData, index) => {
                      const uniquePanelKey = `panel-source-${sourceData.type}-${
                        sourceData.type === "PDF"
                          ? `${sourceData.source}-page-${sourceData.page}-${index}`
                          : sourceData.type === "RUBRIC"
                          ? `rubric-${sourceData.rubric || index}`
                          : sourceData.type === "VIDEO"
                          ? `video-${sourceData.video_url || index}`
                          : `article-${sourceData.id || index}`
                      }`;

                      return (
                        <React.Fragment key={uniquePanelKey}>
                          {sourceData.type === "RUBRIC" ? (
                            sourceData.data?.[0] && (
                              <ContentCard
                                user={user}
                                type={sourceData.type}
                                question={sourceData.data[0].titleFr}
                                publishDate={
                                  sourceData.data[0].questions?.[0]?.updatedAt
                                    ?.date ||
                                  sourceData.data[0].questions?.[0]?.createdAt
                                    ?.date
                                }
                                chain={sourceData.data[0].chain}
                                author={sourceData.data[0].author}
                                rubric={sourceData.data[0]}
                                similarity={sourceData.similarity}
                                recency={sourceData.recency}
                                score={sourceData.score}
                              />
                            )
                          ) : sourceData.type === "VIDEO" ? (
                            sourceData && (
                              <ContentCard
                                type={sourceData.type}
                                url={sourceData.video_url}
                                startTime={sourceData.start_time}
                                endTime={sourceData.end_time}
                                event={sourceData.event_id}
                                user={user}
                                token={token}
                                userEventsAccess={userEventsAccess}
                              />
                            )
                          ) : sourceData.type === "ARTICLE" ? (
                            sourceData.data?.[0] && (
                              <ContentCard
                                user={user}
                                type={sourceData.type}
                                question={sourceData.data[0].title}
                                publishDate={
                                  sourceData.data[0].updatedAt ||
                                  sourceData.data[0].publishedAt
                                }
                                chain={sourceData.data[0].chains}
                                author={sourceData.data[0].author}
                                id={sourceData.data[0].id}
                                similarity={sourceData.similarity}
                                recency={sourceData.recency}
                                score={sourceData.score}
                                token={token}
                              />
                            )
                          ) : (
                            <ContentCard
                              user={user}
                              type={sourceData.type}
                              question={formatSourceName(sourceData.source)}
                              page={sourceData.page}
                              target_page={sourceData.target_page}
                              url={sourceData.url}
                              coverUrl={sourceData.cover_url}
                              similarity={sourceData.similarity}
                              recency={sourceData.recency}
                              score={sourceData.score}
                              name={sourceData.name}
                              extra={sourceData.extra}
                              extra_pages={sourceData.extra_pages}
                              panel={true}
                              token={token}
                              content={sourceData.content}
                            />
                          )}
                        </React.Fragment>
                      );
                    }
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showActiveSilosModal &&
        createPortal(
          <div
            className="silo-modal-overlay"
            onClick={() => setShowActiveSilosModal(false)}
          >
            <div
              className="silo-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="silo-modal-header"
                style={{ borderBottom: "none" }}
              >
                <div className="silo-modal-title-container">
                  <IconDb className="silo-modal-db-icon" />
                  <h2 className="silo-modal-title">
                    Ressources activées : ({countActiveResources()})
                  </h2>
                </div>
                <button
                  onClick={() => setShowActiveSilosModal(false)}
                  className="silo-modal-close-button-icon"
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="silo-list">
                {getActiveSiloObjects().length > 0 ? (
                  getActiveSiloObjects().map((res) => (
                    <DisplaySiloCard key={res.id || res.name} res={res} />
                  ))
                ) : (
                  <div
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      color: "#6B7280",
                    }}
                  >
                    Aucune ressource trouvée
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

UserQuestion.propTypes = {
  question: PropTypes.string.isRequired,
  review: PropTypes.shape({
    answer: PropTypes.string,
    sources: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        title: PropTypes.string,
        content: PropTypes.string,
        url: PropTypes.string,
        page: PropTypes.number,
      })
    ),
  }),
  avatar: PropTypes.string,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    institute: PropTypes.string,
    numeroAgreation: PropTypes.string,
  }).isRequired,
  token: PropTypes.string.isRequired,
  onChatPosted: PropTypes.func,
  onRatingComplete: PropTypes.func.isRequired,
  setInputDisabled: PropTypes.func.isRequired,
  model: PropTypes.string,
  customPrompt: PropTypes.string,
};

export default UserQuestion;
